import React, { useState, useRef } from 'react';
import { 
  ChevronRight, Check, Phone, Mail, MapPin, User, Building2,
  Utensils, BarChart3, Globe, Zap, Clock, Shield, ArrowRight,
  Monitor, Smartphone, Tablet, ChevronDown, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = 'https://images.unsplash.com/photo-1762806883627-4bcbfad98a2c?w=1920&q=80';
const REST_IMG = 'https://images.pexels.com/photos/776538/pexels-photo-776538.jpeg?auto=compress&cs=tinysrgb&w=1260';

const FEATURES = [
  {
    icon: Utensils,
    title: 'Tu mejor camarero',
    desc: 'Carta digital inteligente en 4 idiomas. Recomienda según la hora del día. Atiende todas las mesas a la vez, sin errores, sin formación.',
    color: '#B78C3C',
    items: ['Carta multilingüe (ES, CAT, EN, FR)', 'Modo Momento: recomendaciones por franja horaria', 'Ticket digital + email automático', 'Alérgenos siempre visibles']
  },
  {
    icon: BarChart3,
    title: 'Tu mejor administrador',
    desc: 'Envío directo a cocina y barra. Caja automática. Escaneo de facturas con IA. Fichaje de empleados. Todo sin papel.',
    color: '#7B8F6B',
    items: ['Pedidos directos a cocina/barra por sección', 'Cierre de caja y reporting automático', 'Escaneo de facturas (foto o PDF)', 'Control de fichaje de empleados']
  },
  {
    icon: Zap,
    title: 'Tu mejor estratega',
    desc: 'Upsell automático que sube el ticket medio. Captación de clientes RGPD. Datos reales de qué vendes y cuándo.',
    color: '#D4763C',
    items: ['Turbo Marketing: upsell al añadir productos', 'Sorteo + captación de base de datos (RGPD)', 'Analítica de ventas y productos', 'Venta Rápida: POS directo para barra']
  }
];

const STATS = [
  { value: '+12%', label: 'Ticket medio', sub: 'con upsell automático' },
  { value: '-40%', label: 'Errores comanda', sub: 'envío directo a cocina' },
  { value: '4', label: 'Idiomas', sub: 'sin camarero políglota' },
  { value: '0€', label: 'Hardware', sub: 'solo navegador' },
];

const STEPS = [
  { num: '01', title: 'Configura', desc: 'Tu carta, tu estrategia, tus reglas. En 30 minutos lo tenemos todo listo.' },
  { num: '02', title: 'Escanea', desc: 'Pon el QR en las mesas. Tus clientes acceden desde su propio móvil.' },
  { num: '03', title: 'Crece', desc: 'El sistema vende, gestiona y capta por ti. 24/7, sin descanso.' },
];

const PRICING = [
  { 
    name: 'Starter', price: '89', period: '/mes',
    desc: 'Para empezar a digitalizar',
    features: ['Carta digital multilingüe', 'QR para mesas', 'Envío a cocina/barra', 'Fichaje de empleados', 'Ticket digital'],
    cta: 'Empezar', highlight: false
  },
  { 
    name: 'Growth', price: '149', period: '/mes',
    desc: 'Para vender más en cada mesa',
    features: ['Todo de Starter', 'Modo Momento', 'Turbo Marketing (upsell)', 'Sorteo + captación RGPD', 'Reporting y analítica', 'Venta Rápida (POS)'],
    cta: 'Elegir Growth', highlight: true
  },
  { 
    name: 'Premium', price: '249', period: '/mes',
    desc: 'Para control total del negocio',
    features: ['Todo de Growth', 'Escaneo de facturas IA', 'Integraciones avanzadas', 'Soporte prioritario', 'Personalización a medida', 'Multi-local'],
    cta: 'Contactar', highlight: false
  },
];

const FAQ = [
  { q: '¿Necesito comprar hardware?', a: 'No. Pont10 funciona desde cualquier dispositivo con navegador: tu móvil, una tablet, un portátil. Sin apps, sin instalaciones.' },
  { q: '¿Puedo usar mi tablet vieja de cocina?', a: 'Sí. Si abre un navegador, funciona. Chrome, Safari, Firefox — da igual.' },
  { q: '¿Cuánto tardo en estar operativo?', a: 'En 48 horas tienes todo configurado: carta, QR, cocina y barra. Te acompañamos en el proceso.' },
  { q: '¿Y si ya tengo un TPV?', a: 'Pont10 complementa tu TPV actual. No tienes que cambiar nada. Con el tiempo, muchos clientes lo usan como sistema principal.' },
  { q: '¿Es seguro para los datos de mis clientes?', a: 'Sí. Cumplimos RGPD al 100%. Los datos se almacenan de forma segura y los consentimientos están diferenciados.' },
  { q: '¿Funciona para varios locales?', a: 'Sí. El plan Premium soporta multi-local con configuración independiente para cada uno.' },
];

const B2BLanding = () => {
  const [form, setForm] = useState({ name: '', business_name: '', phone: '', city: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const formRef = useRef(null);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business_name || !form.phone) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/b2b/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) setSubmitted(true);
    } catch (e) { /* retry */ }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--sand-50)', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>

      {/* ─── NAV ─── */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(251,245,232,0.8)', borderColor: 'rgba(183,140,60,0.12)' }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/images/logo.png" alt="Pont10" className="h-8" />
          </div>
          <button
            onClick={scrollToForm}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-105"
            style={{ backgroundColor: 'var(--stone-800)' }}
            data-testid="nav-cta"
          >
            Solicitar demo
          </button>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative pt-16 min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--stone-900)]/95 via-[var(--stone-900)]/80 to-[var(--stone-900)]/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm mb-6 text-sm text-white/80">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Sistema en producción real
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 tracking-tight">
              El sistema 360° que tu restaurante necesita
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mb-8 leading-relaxed max-w-xl">
              Camarero, administrador y estratega comercial. Todo desde el navegador. Sin hardware, sin instalaciones, sin límites.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={scrollToForm}
                className="px-8 py-4 rounded-full text-base font-semibold text-[#3D2E1F] transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#B78C3C' }}
                data-testid="hero-cta"
              >
                Solicitar demo gratuita <ArrowRight size={18} />
              </button>
              <a href="#como-funciona"
                className="px-8 py-4 rounded-full text-base font-medium text-white border border-white/20 hover:bg-white/10 transition-all text-center"
              >
                Ver cómo funciona
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROBLEMA ─── */}
      <section className="py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#B78C3C' }}>El problema</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-12" style={{ color: '#3D2E1F' }}>
            ¿Tu día a día es así?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { text: 'Formas camareros que se van en 3 meses', sub: 'Rotación del 73%. Cada nuevo empleado son semanas de productividad perdida.' },
              { text: 'Nadie hace upsell excepto tú', sub: 'Tu camarero toma nota. No recomienda, no sugiere, no vende.' },
              { text: 'Cierras caja a las 2AM y metes facturas el domingo', sub: 'Horas de administración que no generan ingresos.' },
              { text: 'Dependes de hardware caro que cuando falla, te paraliza', sub: 'TPV propietario, impresoras, tablets exclusivas. Un fallo = servicio parado.' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl border border-[#3D2E1F]/8 hover:border-[#B78C3C]/30 transition-colors group">
                <p className="text-lg font-semibold mb-2" style={{ color: '#3D2E1F' }}>{item.text}</p>
                <p className="text-sm leading-relaxed" style={{ color: '#7A6B4E' }}>{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3 DIMENSIONES ─── */}
      <section className="py-20 md:py-28 px-5" style={{ backgroundColor: '#3D2E1F' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#B78C3C' }}>La solución</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Un sistema. Tres superpoderes.
          </h2>
          <p className="text-base text-white/50 mb-14 max-w-2xl">
            Pont10 clona la inteligencia de tu negocio y la ejecuta automáticamente en cada mesa, cada hora, cada día.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/8 hover:border-white/15 transition-all group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${f.color}20` }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-white/50 mb-5 leading-relaxed">{f.desc}</p>
                <ul className="space-y-2.5">
                  {f.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-white/70">
                      <Check size={15} className="mt-0.5 shrink-0" style={{ color: f.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ZERO HARDWARE ─── */}
      <section className="py-20 md:py-28 px-5 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#B78C3C' }}>Libertad total</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6" style={{ color: '#3D2E1F' }}>
                Solo necesitas un navegador
              </h2>
              <p className="text-base mb-8 leading-relaxed" style={{ color: '#7A6B4E' }}>
                Tu móvil, una tablet de 80€, el portátil de casa. Si abre Chrome, funciona. 
                Si se rompe un dispositivo, coges otro y sigues en 30 segundos.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Monitor, text: 'Panel de admin desde el portátil' },
                  { icon: Tablet, text: 'Cocina y barra desde cualquier tablet' },
                  { icon: Smartphone, text: 'Camarero desde su propio móvil' },
                  { icon: Globe, text: 'Cliente desde su móvil (QR)' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#B78C3C15' }}>
                      <item.icon size={18} style={{ color: '#B78C3C' }} />
                    </div>
                    <span className="text-sm font-medium" style={{ color: '#3D2E1F' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="p-6 rounded-2xl bg-[#3D2E1F] text-white">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-4">Comparativa de inversión</p>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm"><span className="text-white/50 line-through">TPV propietario</span><span className="text-white/50 line-through">2.000€</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/50 line-through">Tablet exclusiva</span><span className="text-white/50 line-through">400€</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/50 line-through">Impresora tickets</span><span className="text-white/50 line-through">300€</span></div>
                  <div className="flex justify-between text-sm"><span className="text-white/50 line-through">Licencia mensual</span><span className="text-white/50 line-through">200€/mes</span></div>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Pont10</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold" style={{ color: '#B78C3C' }}>89€</span>
                      <span className="text-sm text-white/50">/mes</span>
                    </div>
                  </div>
                  <p className="text-xs text-white/30 mt-1">Sin hardware. Sin permanencia. Sin sorpresas.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="py-16 px-5" style={{ backgroundColor: '#F0E4CE' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: '#3D2E1F' }}>{s.value}</p>
              <p className="text-sm font-semibold mb-0.5" style={{ color: '#3D2E1F' }}>{s.label}</p>
              <p className="text-xs" style={{ color: '#7A6B4E' }}>{s.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CÓMO FUNCIONA ─── */}
      <section id="como-funciona" className="py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#B78C3C' }}>Así de fácil</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-14" style={{ color: '#3D2E1F' }}>
            Operativo en 48 horas
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={i} className="relative">
                <span className="text-6xl font-bold" style={{ color: '#B78C3C', opacity: 0.15 }}>{s.num}</span>
                <h3 className="text-xl font-bold -mt-4 mb-2" style={{ color: '#3D2E1F' }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#7A6B4E' }}>{s.desc}</p>
                {i < 2 && <ChevronRight size={20} className="hidden md:block absolute top-8 -right-4 text-[#B78C3C]/30" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CASO REAL ─── */}
      <section className="py-20 md:py-28 px-5" style={{ backgroundColor: '#3D2E1F' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img src={REST_IMG} alt="Bistró Pont 10" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#B78C3C' }}>Caso real</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Bistró Pont 10
              </h2>
              <p className="text-base text-white/60 mb-6 leading-relaxed">
                El primer restaurante que usa el sistema completo en producción real. 
                Carta en 4 idiomas, upsell automático, captación RGPD, envío directo a cocina y barra.
              </p>
              <blockquote className="border-l-2 pl-4 mb-8" style={{ borderColor: '#B78C3C' }}>
                <p className="text-white/80 italic leading-relaxed">
                  "Desde que usamos el sistema, cada mesa vende más sin que hagamos nada diferente. 
                  La carta recomienda sola y los camareros nuevos rinden desde el primer día."
                </p>
              </blockquote>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 rounded-xl bg-white/5">
                  <p className="text-xl font-bold" style={{ color: '#B78C3C' }}>4</p>
                  <p className="text-xs text-white/40">idiomas</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/5">
                  <p className="text-xl font-bold" style={{ color: '#B78C3C' }}>360°</p>
                  <p className="text-xs text-white/40">gestión</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-white/5">
                  <p className="text-xl font-bold" style={{ color: '#B78C3C' }}>0€</p>
                  <p className="text-xs text-white/40">hardware</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className="py-20 md:py-28 px-5">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 text-center" style={{ color: '#B78C3C' }}>Planes</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-center" style={{ color: '#3D2E1F' }}>
            Elige tu plan
          </h2>
          <p className="text-base text-center mb-14 max-w-lg mx-auto" style={{ color: '#7A6B4E' }}>
            Sin permanencia. Sin hardware. Empieza con -50% los 3 primeros meses.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING.map((p, i) => (
              <div key={i} className={`p-6 rounded-2xl border-2 transition-all ${p.highlight ? 'border-[#B78C3C] shadow-lg scale-[1.02]' : 'border-[#3D2E1F]/8'}`}>
                {p.highlight && (
                  <div className="text-xs font-bold uppercase tracking-widest text-center py-1 px-3 rounded-full mb-4 inline-block" style={{ backgroundColor: '#B78C3C15', color: '#B78C3C' }}>
                    Más popular
                  </div>
                )}
                <h3 className="text-xl font-bold mb-1" style={{ color: '#3D2E1F' }}>{p.name}</h3>
                <p className="text-sm mb-4" style={{ color: '#7A6B4E' }}>{p.desc}</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold" style={{ color: '#3D2E1F' }}>{p.price}€</span>
                  <span className="text-sm" style={{ color: '#7A6B4E' }}>{p.period}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm" style={{ color: '#3D2E1F' }}>
                      <Check size={15} className="mt-0.5 shrink-0" style={{ color: '#B78C3C' }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToForm}
                  className={`w-full py-3 rounded-full text-sm font-semibold transition-all hover:scale-105 ${
                    p.highlight 
                      ? 'text-white' 
                      : 'border-2 text-[#3D2E1F] hover:bg-[#3D2E1F]/5'
                  }`}
                  style={p.highlight ? { backgroundColor: '#B78C3C' } : { borderColor: '#3D2E1F' }}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-28 px-5" style={{ backgroundColor: '#F0E4CE' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center" style={{ color: '#3D2E1F' }}>
            Preguntas frecuentes
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="rounded-xl bg-white overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm font-semibold pr-4" style={{ color: '#3D2E1F' }}>{item.q}</span>
                  <ChevronDown size={18} className={`shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: '#7A6B4E' }} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-sm leading-relaxed" style={{ color: '#7A6B4E' }}>{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FORMULARIO ─── */}
      <section ref={formRef} className="py-20 md:py-28 px-5" style={{ backgroundColor: '#3D2E1F' }}>
        <div className="max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 text-center" style={{ color: '#B78C3C' }}>Siguiente paso</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 text-center">
            Solicita tu demo gratuita
          </h2>
          <p className="text-base text-white/50 mb-10 text-center">
            15 minutos para ver cómo Pont10 transformará tu negocio
          </p>

          {submitted ? (
            <div className="text-center py-12 rounded-2xl bg-white/5" data-testid="lead-success">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Solicitud recibida</h3>
              <p className="text-white/50">Te contactaremos en menos de 24 horas para agendar la demo.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="b2b-lead-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input type="text" placeholder="Tu nombre" required value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#B78C3C]/50"
                    data-testid="lead-name"
                  />
                </div>
                <div className="relative">
                  <Building2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input type="text" placeholder="Nombre del local" required value={form.business_name}
                    onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#B78C3C]/50"
                    data-testid="lead-business"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input type="tel" placeholder="Teléfono" required value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#B78C3C]/50"
                    data-testid="lead-phone"
                  />
                </div>
                <div className="relative">
                  <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input type="text" placeholder="Ciudad" value={form.city}
                    onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#B78C3C]/50"
                    data-testid="lead-city"
                  />
                </div>
              </div>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="email" placeholder="Email (opcional)" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#B78C3C]/50"
                  data-testid="lead-email"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-full text-base font-semibold text-[#3D2E1F] transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{ backgroundColor: '#B78C3C' }}
                data-testid="lead-submit"
              >
                {submitting ? 'Enviando...' : 'Solicitar demo gratuita'}
              </button>
              <p className="text-xs text-white/20 text-center">Sin compromiso. Te contactamos en menos de 24h.</p>
            </form>
          )}
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-8 px-5 border-t border-[#3D2E1F]/5">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: '#7A6B4E' }}>
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Pont10" className="h-6 opacity-50" />
            <span>Pont10 Technology</span>
          </div>
          <p>pont10.cat</p>
        </div>
      </footer>
    </div>
  );
};

export default B2BLanding;
